import React, { useEffect, useMemo, useState } from "react";
import { buildAuthedJsonBody, getUserFromToken } from "../../../../utils/functions.ts";

const OverviewNotes = ({ FBXID, printSelections = {}, setPrintItemSelection = null }) => {
  const playerId = useMemo(() => {
    const n = Number(FBXID);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [FBXID]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);

  const clean = (v) => {
    if (v === undefined || v === null) return "";
    const s = String(v).trim();
    if (!s) return "";
    const low = s.toLowerCase();
    if (low === "null" || low === "undefined" || low === "n/a" || low === "na") return "";
    if (s === "0000-00-00" || s === "1950-01-01") return "";
    return s;
  };

  const normalizeRow = (r) => {
    const id =
      r?.CommId ??
      r?.commId ??
      r?.ID ??
      r?.id ??
      r?.noteID ??
      r?.NoteID ??
      `${r?.FBXID || ""}-${r?.NoteDate || r?.noteDate || r?.DATE || r?.date || Math.random()}`;

    const date =
      clean(r?.NoteDate) ||
      clean(r?.DateOfContact) ||
      clean(r?.noteDate) ||
      clean(r?.DATE) ||
      clean(r?.date) ||
      clean(r?.createdAt) ||
      clean(r?.created_at) ||
      "-";

    const title =
      clean(r?.Title) ||
      clean(r?.SourceData) ||
      clean(r?.title) ||
      clean(r?.NoteTitle) ||
      clean(r?.noteTitle) ||
      clean(r?.subject) ||
      clean(r?.SUBJECT) ||
      "-";

    const by =
      clean(r?.ByWho) ||
      clean(r?.ByName) ||
      clean(r?.byWho) ||
      clean(r?.createdBy) ||
      clean(r?.CreatedBy) ||
      clean(r?.scoutName) ||
      clean(r?.ScoutName) ||
      clean(r?.displayName) ||
      clean(r?.userName) ||
      clean(r?.user) ||
      clean(r?.WHO) ||
      "-";

    const noteHtml =
      clean(r?.Note) ||
      clean(r?.Communication) ||
      clean(r?.note) ||
      clean(r?.NoteText) ||
      clean(r?.noteText) ||
      clean(r?.Memo) ||
      clean(r?.memo) ||
      clean(r?.Notes) ||
      clean(r?.notes) ||
      "";

    return { id, date, title, by, noteHtml };
  };

  useEffect(() => {
    const controller = new AbortController();

    const fetchNotes = async () => {
      setRows([]);
      setErrMsg("");
      setCurrentIndex(0);

      if (!playerId) return;

      setLoading(true);

      const token = await getUserFromToken().catch(() => null);

      if (!token?.team) {
        setLoading(false);
        setErrMsg("Missing user/team token.");
        return;
      }

      const payload = {
        FBXID: playerId,
        myTeam: token.team,
        script: "getNotes",
      };

      try {
        const res = await fetch("https://www.fbxchange.com/ReactPHP/overview.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal: controller.signal,
          body: buildAuthedJsonBody(payload),
        });

        const raw = await res.text();

        let data;
        try {
          data = JSON.parse(raw);
        } catch (e) {
          setErrMsg("Backend did not return JSON.");
          setLoading(false);
          return;
        }

        const arr = Array.isArray(data) ? data : data?.rows || [];
        const normalized = (Array.isArray(arr) ? arr : []).map(normalizeRow);

        setRows(normalized);
        setCurrentIndex(0);
        setLoading(false);
      } catch (e) {
        if (e?.name === "AbortError") return;
        setErrMsg("Network error loading notes.");
        setLoading(false);
      }
    };

    fetchNotes();

    return () => {
      controller.abort();
    };
  }, [playerId]);

  useEffect(() => {
    if (!rows.length) {
      setCurrentIndex(0);
      return;
    }
    if (currentIndex > rows.length - 1) {
      setCurrentIndex(rows.length - 1);
    }
  }, [rows, currentIndex]);

  const currentNote = rows[currentIndex] || null;
  const canGoBack = currentIndex > 0;
  const canGoNext = currentIndex < rows.length - 1;
  const currentNotePrintKey = currentNote ? `note-${currentNote.id}` : "";
  const currentNoteWillPrint = !!printSelections.notes && printSelections[currentNotePrintKey] !== false;

  const goBack = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const goNext = () => {
    setCurrentIndex((prev) => (prev < rows.length - 1 ? prev + 1 : prev));
  };

  const toggleCurrentNotePrint = () => {
    if (!currentNotePrintKey || !setPrintItemSelection) return;
    setPrintItemSelection("notes", currentNotePrintKey, !currentNoteWillPrint);
  };

  return (
    <div className="container-fluid p-0 h-100">
      <div className="row g-0 h-100">
        <div className="col-48 h-100 d-flex flex-column">
          <div
            className="row g-1 bg-navy text-lemon fs-7 py-2 px-2 align-items-center"
            style={{ position: "sticky", top: 0, zIndex: 25 }}
          >
            <div className="col-20">
              <strong>Player Notes</strong>
            </div>

            <div className="col-12 text-center">
              {!loading && !errMsg && rows.length > 0 ? (
                <span>
                  {currentIndex + 1} / {rows.length}
                </span>
              ) : (
                <span>-</span>
              )}
            </div>

            <div className="col-16 text-end d-flex justify-content-end gap-1">
              <button
                type="button"
                className={`btn btn-sm ${currentNoteWillPrint ? "btn-success" : "btn-danger"}`}
                onClick={toggleCurrentNotePrint}
                disabled={!currentNote || loading || !!errMsg || rows.length === 0 || !setPrintItemSelection}
                title={currentNoteWillPrint ? "Leave this note out of the overview print" : "Include this note in the overview print"}
              >
                {currentNoteWillPrint ? "Print" : "Will Not Print"}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                onClick={goBack}
                disabled={!canGoBack || loading || !!errMsg || rows.length === 0}
              >
                Back
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                onClick={goNext}
                disabled={!canGoNext || loading || !!errMsg || rows.length === 0}
              >
                Next
              </button>
            </div>
          </div>

          <div className="p-2 flex-grow-1" style={{ minHeight: 0 }}>
            {loading ? (
              <div className="text-muted fs-7">
                <em>Loading notes...</em>
              </div>
            ) : errMsg ? (
              <div className="alert alert-warning py-2 mb-0 fs-7">{errMsg}</div>
            ) : rows.length === 0 ? (
              <div className="text-muted small">
                <em>No notes found.</em>
              </div>
            ) : (
              <div
                className="bg-white border rounded shadow-sm h-100 d-flex flex-column"
                style={{ minHeight: 0 }}
              >
                <div className="w-100 px-2 py-2 border-bottom border-1 border-navy">
                  <div className="row g-2 align-items-end">
                    <div className="col-8">
                      <label className="form-label mb-1 small text-muted">Date</label>
                      <input
                        className="form-control form-control-sm"
                        value={currentNote?.date || ""}
                        disabled
                      />
                    </div>

                    <div className="col-28">
                      <label className="form-label mb-1 small text-muted">Title</label>
                      <input
                        className="form-control form-control-sm"
                        value={currentNote?.title || ""}
                        disabled
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label mb-1 small text-muted">By</label>
                      <input
                        className="form-control form-control-sm"
                        value={currentNote?.by || ""}
                        disabled
                      />
                    </div>
                  </div>
                </div>

                <div className="px-2 pb-3 d-flex flex-column" style={{ flex: 1, minHeight: 0 }}>
                  <div
                    className="mt-2 border rounded w-100 d-flex flex-column bg-white"
                    style={{
                      flex: 1,
                      minHeight: 0,
                      overflowY: "auto",
                      overflowX: "hidden",
                    }}
                  >
                    <div className="row g-2 p-2">
                      <div className="col-48">
                        <label className="form-label mb-1 small text-muted">Note</label>

                        <div
                          className="form-control"
                          style={{
                            minHeight: "360px",
                            height: "100%",
                            overflowY: "auto",
                            whiteSpace: "normal",
                            backgroundColor: "#fff",
                          }}
                          dangerouslySetInnerHTML={{
                            __html: currentNote?.noteHtml || "<em>No note content.</em>",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewNotes;
